import React, { useState, useEffect, useCallback } from 'react';
import { investApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, MagnifyingGlass, Storefront, HandCoins, Briefcase, Check, X, Trash,
  MapPin, Users as UsersIcon, Buildings, TrendUp, CurrencyDollar,
} from '@phosphor-icons/react';
import { ObjectPageHeader, SectionTabs, FioriTable, StatusPill, STATUS_COLOR, fmtCurrency } from '../components/fiori';
import { useAuth } from '../contexts/AuthContext';

const TABS = [
  { key: 'browse',    label: 'Browse marketplace' },
  { key: 'listings',  label: 'My listings (sell)' },
  { key: 'bids',      label: 'My bids (buy)' },
  { key: 'received',  label: 'Bids received' },
  { key: 'portfolio', label: 'Portfolio' },
];

export default function InvestPage() {
  const { user } = useAuth();
  const [active, setActive] = useState('browse');
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadSummary = useCallback(async () => {
    try { const r = await investApi.summary(); setSummary(r.data); } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let r;
      if (active === 'browse')    r = await investApi.listings.list({ status: 'active' });
      else if (active === 'listings')  r = await investApi.listings.list();
      else if (active === 'bids')      r = await investApi.myBids();
      else if (active === 'received')  r = await investApi.receivedBids();
      else if (active === 'portfolio') r = await investApi.portfolio();
      let data = r?.data || [];
      // Filter "My listings" to only mine
      if (active === 'listings' && user?.id) data = data.filter(l => l.owner_id === user.id);
      setRows(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [active, user]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  const kpis = [
    { label: 'Active listings',   value: summary.my_active_listings ?? 0 },
    { label: 'Bids placed',       value: summary.bids_placed ?? 0, color: '#41B6E6' },
    { label: 'Bids received',     value: summary.bids_received ?? 0, color: '#FFC72C' },
    { label: 'Portfolio value',   value: fmtCurrency(summary.portfolio_value || 0), color: '#00B140' },
  ];

  return (
    <div data-testid="invest-page">
      <ObjectPageHeader
        breadcrumb={['Home', 'Invest']}
        title="Invest"
        subtitle="Marketplace for buying and selling businesses"
        kpis={kpis}
        actions={
          <Button
            className="rounded-sm h-9"
            style={{ background: '#002677' }}
            onClick={() => setDialog('listing')}
            data-testid="invest-create-btn"
          >
            <Plus size={16} className="mr-1.5" /> Sell a business
          </Button>
        }
      />
      <SectionTabs tabs={TABS} active={active} onChange={setActive} />

      <div className="p-6">
        {loading ? (
          <div className="text-center py-10 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            {active === 'browse' && (
              <ListingGrid rows={rows} onOpen={(l) => setDetail(l)} />
            )}
            {active === 'listings' && (
              <MyListingsTable rows={rows} reload={load} />
            )}
            {active === 'bids' && (
              <MyBidsTable rows={rows} reload={load} />
            )}
            {active === 'received' && (
              <ReceivedBidsTable rows={rows} reload={load} />
            )}
            {active === 'portfolio' && (
              <PortfolioGrid rows={rows} />
            )}
          </>
        )}
      </div>

      {dialog === 'listing' && (
        <ListingForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />
      )}
      {detail && (
        <ListingDetail
          listing={detail}
          onClose={() => setDetail(null)}
          onBidPlaced={() => { setDetail(null); loadSummary(); }}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}

/* ------------- Browse grid ------------- */

function ListingGrid({ rows, onOpen }) {
  if (rows.length === 0) return <Empty label="No active listings yet" />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map((l) => (
        <button
          key={l.id}
          onClick={() => onOpen(l)}
          data-testid={`listing-${l.id}`}
          className="bg-card border border-border rounded-sm p-4 text-left hover:shadow-md transition-shadow relative"
          style={{ borderLeft: `3px solid ${STATUS_COLOR[l.status] || '#002677'}` }}        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading font-semibold text-sm truncate">{l.title}</h3>
            <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#002677' }}>{l.listing_type}</span>
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            {l.sector    && <span className="flex items-center gap-1"><Buildings size={11} />{l.sector}</span>}
            {l.location  && <span className="flex items-center gap-1"><MapPin size={11} />{l.location}</span>}
            {l.employee_count > 0 && <span className="flex items-center gap-1"><UsersIcon size={11} />{l.employee_count}</span>}
          </div>
          <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{l.description}</p>
          <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Asking</div>
              <div className="font-semibold text-sm" style={{ color: '#002677' }}>{fmtCurrency(l.asking_price)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Revenue</div>
              <div className="text-sm">{fmtCurrency(l.annual_revenue)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Profit</div>
              <div className="text-sm" style={{ color: l.annual_profit >= 0 ? '#00B140' : '#E4002B' }}>{fmtCurrency(l.annual_profit)}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ------------- Tables ------------- */

function MyListingsTable({ rows, reload }) {
  const del = async (id) => {
    try { await investApi.listings.delete(id); toast.success('Listing deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const setStatus = async (id, status) => {
    try { await investApi.listings.update(id, { status }); toast.success(`Listing ${status}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'title', label: 'Business' },
        { key: 'sector', label: 'Sector' },
        { key: 'asking_price', label: 'Asking', render: r => <span className="font-semibold">{fmtCurrency(r.asking_price)}</span> },
        { key: 'annual_revenue', label: 'Revenue', render: r => fmtCurrency(r.annual_revenue) },
        { key: 'inquiries', label: 'Inquiries' },
        { key: 'views', label: 'Views' },
        { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
        { key: 'actions', label: '', width: '200px', render: r => (
            <div className="flex gap-1 justify-end">
              {r.status === 'draft'    && <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'active')}>Publish</Button>}
              {r.status === 'active'   && <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'withdrawn')}>Withdraw</Button>}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => del(r.id)}><Trash size={14} /></Button>
            </div>
          ) },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="You haven't listed a business yet — click 'Sell a business'"
    />
  );
}

function MyBidsTable({ rows, reload }) {
  const withdraw = async (id) => {
    try { await investApi.setBidStatus(id, 'withdrawn'); toast.success('Bid withdrawn'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'listing_title', label: 'Business' },
        { key: 'amount', label: 'Your bid', render: r => <span className="font-semibold">{fmtCurrency(r.amount)}</span> },
        { key: 'created_at', label: 'Placed', render: r => r.created_at?.slice(0, 10) },
        { key: 'message', label: 'Message', render: r => <span className="text-muted-foreground truncate block max-w-xs">{r.message || '—'}</span> },
        { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
        { key: 'actions', label: '', width: '120px', render: r => (
            r.status === 'pending' ? <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => withdraw(r.id)}>Withdraw</Button> : null
          ) },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="You haven't placed any bids"
    />
  );
}

function ReceivedBidsTable({ rows, reload }) {
  const respond = async (id, status) => {
    try { await investApi.setBidStatus(id, status); toast.success(`Bid ${status}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'listing_title', label: 'Business' },
        { key: 'buyer_name', label: 'Bidder' },
        { key: 'amount', label: 'Offer', render: r => <span className="font-semibold" style={{ color: '#002677' }}>{fmtCurrency(r.amount)}</span> },
        { key: 'created_at', label: 'Received', render: r => r.created_at?.slice(0, 10) },
        { key: 'message', label: 'Message', render: r => <span className="text-muted-foreground truncate block max-w-xs">{r.message || '—'}</span> },
        { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
        { key: 'actions', label: '', width: '180px', render: r => (
            r.status === 'pending' ? (
              <div className="flex gap-1 justify-end">
                <Button size="sm" className="h-7 text-xs rounded-sm" style={{ background: '#00B140' }} onClick={() => respond(r.id, 'accepted')}><Check size={12} className="mr-1" />Accept</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => respond(r.id, 'rejected')}><X size={12} className="mr-1" />Reject</Button>
              </div>
            ) : null
          ) },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="No bids received yet"
    />
  );
}

function PortfolioGrid({ rows }) {
  if (rows.length === 0) return <Empty label="No acquired businesses yet — win a bid to build your portfolio." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map((b) => (
        <div key={b.id} className="bg-card border border-border rounded-sm p-4" style={{ borderLeft: '3px solid #00B140' }} data-testid={`portfolio-${b.id}`}>
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading font-semibold text-sm truncate">{b.listing_title}</h3>
            <StatusPill status="acquired" />
          </div>
          <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-3">
            {b.listing?.sector   && <span>{b.listing.sector}</span>}
            {b.listing?.location && <span className="flex items-center gap-1"><MapPin size={11} />{b.listing.location}</span>}
            {b.listing?.employee_count > 0 && <span className="flex items-center gap-1"><UsersIcon size={11} />{b.listing.employee_count}</span>}
          </div>
          <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</div>
              <div className="font-semibold text-sm" style={{ color: '#002677' }}>{fmtCurrency(b.amount)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Revenue</div>
              <div className="text-sm">{fmtCurrency(b.listing?.annual_revenue || 0)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Profit</div>
              <div className="text-sm" style={{ color: '#00B140' }}>{fmtCurrency(b.listing?.annual_profit || 0)}</div>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-muted-foreground">Acquired {b.responded_at?.slice(0, 10)}</div>
        </div>
      ))}
    </div>
  );
}

/* ------------- Listing detail with bid/inquire ------------- */

function ListingDetail({ listing, onClose, onBidPlaced, currentUserId }) {
  const [mode, setMode] = useState(null); // 'bid' | 'inquire'
  const [form, setForm] = useState({ amount: listing.asking_price || '', message: '' });
  const [saving, setSaving] = useState(false);
  const isMine = currentUserId && listing.owner_id === currentUserId;

  const placeBid = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await investApi.listings.placeBid(listing.id, { amount: parseFloat(form.amount), message: form.message });
      toast.success('Bid placed — the seller will be notified');
      onBidPlaced();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const inquire = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await investApi.listings.inquire(listing.id, { message: form.message });
      toast.success('Inquiry sent to the seller');
      onBidPlaced();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Storefront size={20} className="text-[#002677]" />{listing.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {listing.sector    && <span className="flex items-center gap-1"><Buildings size={11} />{listing.sector}</span>}
            {listing.location  && <span className="flex items-center gap-1"><MapPin size={11} />{listing.location}</span>}
            {listing.employee_count > 0 && <span className="flex items-center gap-1"><UsersIcon size={11} />{listing.employee_count} employees</span>}
            <StatusPill status={listing.status} />
          </div>

          <p className="text-sm">{listing.description}</p>

          <div className="grid grid-cols-3 gap-4 bg-muted/30 p-4 rounded-sm border border-border">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Asking price</div>
              <div className="text-xl font-bold" style={{ color: '#002677' }}>{fmtCurrency(listing.asking_price)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Annual revenue</div>
              <div className="text-xl font-bold">{fmtCurrency(listing.annual_revenue)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Annual profit</div>
              <div className="text-xl font-bold" style={{ color: listing.annual_profit >= 0 ? '#00B140' : '#E4002B' }}>{fmtCurrency(listing.annual_profit)}</div>
            </div>
          </div>

          {listing.highlights?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Highlights</div>
              <ul className="space-y-1 text-sm">
                {listing.highlights.map((h, i) => <li key={i} className="flex items-start gap-2"><TrendUp size={14} className="text-[#00B140] flex-shrink-0 mt-0.5" />{h}</li>)}
              </ul>
            </div>
          )}

          {!isMine && !mode && (
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button className="flex-1 rounded-sm" style={{ background: '#002677' }} onClick={() => setMode('bid')} data-testid="bid-btn">
                <HandCoins size={16} className="mr-1.5" /> Place a bid
              </Button>
              <Button variant="outline" className="flex-1 rounded-sm" onClick={() => setMode('inquire')} data-testid="inquire-btn">
                Ask a question
              </Button>
            </div>
          )}

          {mode === 'bid' && (
            <form onSubmit={placeBid} className="space-y-3 pt-2 border-t border-border" data-testid="bid-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Your bid amount</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Message to seller (optional)</Label>
                <Textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Deal terms, timeline, financing..." />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-sm flex-1" onClick={() => setMode(null)}>Back</Button>
                <Button type="submit" className="rounded-sm flex-1" style={{ background: '#002677' }} disabled={saving} data-testid="submit-bid">
                  {saving ? 'Placing bid…' : 'Submit bid'}
                </Button>
              </div>
            </form>
          )}

          {mode === 'inquire' && (
            <form onSubmit={inquire} className="space-y-3 pt-2 border-t border-border" data-testid="inquire-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">Your question</Label>
                <Textarea rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required placeholder="Ask about financials, team, customers, growth..." />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-sm flex-1" onClick={() => setMode(null)}>Back</Button>
                <Button type="submit" className="rounded-sm flex-1" style={{ background: '#002677' }} disabled={saving}>
                  {saving ? 'Sending…' : 'Send inquiry'}
                </Button>
              </div>
            </form>
          )}

          {isMine && (
            <div className="text-xs text-muted-foreground pt-2 border-t border-border">This is your own listing. View incoming bids under "Bids received".</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------- Create-listing form ------------- */

function ListingForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', listing_type: 'sell', sector: '', location: '',
    description: '', asking_price: '', annual_revenue: '', annual_profit: '',
    employee_count: '', status: 'draft',
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await investApi.listings.create({
        ...form,
        asking_price:   parseFloat(form.asking_price) || 0,
        annual_revenue: parseFloat(form.annual_revenue) || 0,
        annual_profit:  parseFloat(form.annual_profit) || 0,
        employee_count: parseInt(form.employee_count, 10) || 0,
      });
      toast.success('Listing created as draft — publish from My listings when ready');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Sell a business</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <Field label="Business name / title"><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Listing type">
              <Select value={form.listing_type} onValueChange={v => setForm({ ...form, listing_type: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sell">Full sale</SelectItem>
                  <SelectItem value="partnership">Partnership</SelectItem>
                  <SelectItem value="merger">Merger</SelectItem>
                  <SelectItem value="investment">Investment round</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sector"><Input value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })} placeholder="SaaS, Manufacturing, Retail..." /></Field>
          </div>
          <Field label="Location"><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Milan, Italy" /></Field>
          <Field label="Description"><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief pitch, history, ideal buyer..." /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Asking price (€)"><Input type="number" step="0.01" value={form.asking_price} onChange={e => setForm({ ...form, asking_price: e.target.value })} required /></Field>
            <Field label="Annual revenue (€)"><Input type="number" step="0.01" value={form.annual_revenue} onChange={e => setForm({ ...form, annual_revenue: e.target.value })} /></Field>
            <Field label="Annual profit (€)"><Input type="number" step="0.01" value={form.annual_profit} onChange={e => setForm({ ...form, annual_profit: e.target.value })} /></Field>
          </div>
          <Field label="Employees"><Input type="number" value={form.employee_count} onChange={e => setForm({ ...form, employee_count: e.target.value })} /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="rounded-sm" style={{ background: '#002677' }} disabled={saving} data-testid="submit-listing">
              {saving ? 'Saving…' : 'Create draft'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Empty({ label }) {
  return <div className="p-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-sm">{label}</div>;
}
